//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

import { Enums, Activity } from '../../models/common'

import { CreateEditInitParams, BulkEditInitParams } from '../../modules/common/create-edit-popup/create-edit-popup.controller'
import { ColumnModel } from '../../modules/api/api.models'

import { IOpObject, IReferenceType } from '../../models/util/interfaces'
import { OpDataTypeInGroup, OpRoute, OpRouteData } from '../../models/core'

import { ConvertHelper, LocalizationHelper, LogHelper, ToastHelper, CommonHelper } from '../../helpers'

import { DataTypeGroupService, DATA_TYPE_GROUP_SERVICE_NAME } from '../data-type'
import { GridService, GRID_SERVICE_NAME } from '../grid'
import { DetailsOptions, SYSTEM_SERVICE_NAME, SystemService } from '../system'
import { USER_SESSION_SERVICE_NAME, UserSessionService } from '../user-session'


//#region Public Types

export const ROUTE_SERVICE_NAME = 'RouteService'
export const ROUTE_SERVICE_PROVIDER_NAME = 'RouteServiceProvider'

//#endregion


export class RouteService {

    //#region Costs

    public static get LOG_PREFIX(): string { return '[SIMAX.Services.Route]' }
    public static get FILE_NAME_PREFIX(): string { return 'routes' }
    public static get REFERENCE_TYPE(): Enums.ReferenceType { return Enums.ReferenceType.IdRoute }
    public static get SIMAX_MODULE_NAME(): Enums.SimaxModuleName { return Enums.SimaxModuleName.Route }

    //#endregion


    //#region Ctor

    constructor(
        private dataTypeGroupService: DataTypeGroupService,
        private gridService: GridService,
        private systemService: SystemService,
        private userSessionService: UserSessionService
    ) { }

    //#endregion


    //#region Columns

    public getMainGridColumns(): JQueryPromise<ColumnModel[]> {
        return this.gridService
            .getColumnModelByCategory([
                Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__GRID_PARAMS,
                Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__DEFAULT_GRID_PARAMS
            ], RouteService.SIMAX_MODULE_NAME, {
                groupReferenceType: Enums.ReferenceType.IdDistributor,
                groupReferenceValue: this.userSessionService.getLoggedOperator().idDistributor,
            })
    }

    //#endregion


    //#region Delete

    public deleteRoute(idRoute: number): JQueryPromise<void> {
        const toDelete: OpRoute = new OpRoute(null)

        toDelete.idRoute = idRoute

        if (this.userSessionService.getLoggedOperator().profile != null &&
            this.userSessionService.getLoggedOperator().profile.parameters != null) {
            const idRouteStatus = this.userSessionService.getLoggedOperator().profile.parameters.getValue(Enums.DataType.PROFILE_DELETED_ROUTE_ID_ROUTE_STATUS)

            if (idRouteStatus != null) {
                toDelete.dataList.setValue(idRouteStatus, Enums.DataType.HELPER_ID_ROUTE_STATUS)
            }
        }

        this.systemService.showLoadIndicator()

        return this.userSessionService
            .getDataProvider().Route
            .deleteRoute(toDelete)
            .then(() => {
                ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETED, RouteService.SIMAX_MODULE_NAME))
            })
            .fail((error: any) => {
                const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)

                ToastHelper.showError(errorMsg || LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETE_ERROR, RouteService.SIMAX_MODULE_NAME))
                LogHelper.warn(RouteService.LOG_PREFIX, `Unable to delete route. Error:`, error)
            })
            .always(() => {
                this.systemService.hideLoadIndicator()
            })
    }

    //#endregion

    //#region Edit

    public createBulkRouteFromIOpObject(opObject: IOpObject): OpRoute {
        const route: OpRoute = OpRoute.fromIOpObject(opObject)
        const isOperatorModified: boolean = _.some(opObject.dataList.toArray(), (data: OpRouteData) => _.include([Enums.OpChangeState.New, Enums.OpChangeState.Modified], data.opState))

        route.opState = isOperatorModified ? Enums.OpChangeState.Modified : Enums.OpChangeState.Loaded

        return route
    }

    public createRouteFromIOpObject(opObject: IOpObject): OpRoute {
        return OpRoute.fromIOpObject(opObject)
    }

    //#endregion


    //#region Get

    public getRoute(idRoute: number): JQueryPromise<OpRoute> {
        return this.userSessionService
            .getDataProvider().Route
            .getRoute(idRoute)
            .then((route: OpRoute) => {
                if (route != null) {
                    return route
                }

                return $.Deferred().reject(null).promise()
            })
            .fail((error: any) => {
                ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                LogHelper.warn(RouteService.LOG_PREFIX, `Unable to load route for id ${idRoute}`, error)

                return null
            })
    }

    public getMainGridDataSourceParser(): any {
        return (route: OpRoute) => {
            if (route == null) {
                return null
            }

            const additionalFieldsObj: { additionalFields: { key: string, value: any }[] } = {
                additionalFields: []
            }

            if (route.dataList != null && route.dataList.length > 0) {
                // CELL COLOR
                additionalFieldsObj.additionalFields.push({
                    key: GridService.CELL_COLOR_FIELD_NAME,
                    value: route.dataList.getValue(Enums.DataType.HELPER_CELL_COLOR)
                })

                // ROW COLOR
                additionalFieldsObj.additionalFields.push({
                    key: GridService.ROW_COLOR_FIELD_NAME,
                    value: route.dataList.getValue(Enums.DataType.HELPER_ROW_COLOR)
                })

            }

            return additionalFieldsObj
        }
    }

    //#endregion


    //#region Options

    public getCreateEditOptions(referenceObjectId: number = 0, referenceObject: IReferenceType & IOpObject = null): CreateEditInitParams {
        return {
            createEditSelectedTabType: Enums.SimaxCreateEditSelectedTabType.BaseParameters,
            groupReferenceType: Enums.ReferenceType.IdDistributor,
            groupReferenceValue: this.userSessionService.getLoggedOperator().idDistributor,
            idReferenceType: RouteService.REFERENCE_TYPE,
            moduleName: RouteService.SIMAX_MODULE_NAME,
            referenceObject,
            referenceObjectId,
            sections: null,
            useBusinessObjects: true,
        }
    }

    public getBulkCreateEditOptions(gridData: any, columns: any[]): BulkEditInitParams {
        return {
            columnsReorderingDisabled: this.userSessionService.getLoggedOperator().hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.RouteModuleMainGrid, false),
            gridColumns: columns,
            groupReferenceType: Enums.ReferenceType.IdDistributor,
            groupReferenceValue: this.userSessionService.getLoggedOperator().idDistributor,
            idReferenceType: RouteService.REFERENCE_TYPE,
            moduleName: Enums.SimaxModuleName.Route,
            section: null,
            selectedGridRows: gridData,
            useBusinessObjects: true,
        }
    }

    public getDetailsOptions(selectedItemId: number, pageItemsIds: number[]): DetailsOptions {
        return {
            moduleName: RouteService.SIMAX_MODULE_NAME,
            pageItemsIds: pageItemsIds,
            referenceObjectId: selectedItemId,
        }
    }

    //#endregion


    //#region Parameters

    public getBasicParametersDataTypeGroup(): JQueryPromise<OpDataTypeInGroup[]> {
        return this.dataTypeGroupService.getDataTypeInGroupByCategory(
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__PANEL_PARAMS_TO_DISPLAY,
            RouteService.SIMAX_MODULE_NAME
        )
    }

    //#endregion


    //#region Save

    public saveRoute(routeToSave: OpRoute, silent: boolean = false): JQueryPromise<OpRoute> {
        return this.userSessionService
            .getDataProvider().Route
            .saveRoute(routeToSave)
            .then((savedObject: OpRoute) => {
                if (!silent) {
                    ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVED, RouteService.SIMAX_MODULE_NAME))
                    CommonHelper.setValueToLocalStorage(CommonHelper.localStorageVarForSavedEntity.routeSaved, true, 0)
                }

                return savedObject
            })
            .fail((error: any) => {
                if (!silent) {
                    ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVE_ERROR, RouteService.SIMAX_MODULE_NAME))
                }

                LogHelper.warn(RouteService.LOG_PREFIX, `Unable to save route with id ${routeToSave.idRoute}.`, error)

                return $.Deferred().reject(error).promise()
            })
    }

    //#endregion

}

export class RouteServiceProvider implements ng.IServiceProvider {

    //#region IServiceProvider Implementation 

    public $get = [
        DATA_TYPE_GROUP_SERVICE_NAME,
        GRID_SERVICE_NAME,
        SYSTEM_SERVICE_NAME,
        USER_SESSION_SERVICE_NAME,
        function (
            dataTypeGroupService: DataTypeGroupService,
            gridService: GridService,
            systemService: SystemService,
            userSessionService: UserSessionService
        ) {
            return new RouteService(dataTypeGroupService, gridService, systemService, userSessionService)
        }]

    //#endregion
}